<template>
  <div>
    <div v-if="pageLoading" class="text-center mt-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
    </div>
    <div v-else>
      <div class="d-flex align-items-center mt-1 mb-2">
        <b-button variant="flat-secondary" class="mr-1 btn-icon rounded-circle" @click="goBack">
          <feather-icon icon="ArrowLeftIcon" size="24" />
        </b-button>
        <h2 class="mb-0">{{ t('Find Shipment') }}</h2>
      </div>
      <b-card no-body>
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="font-weight-bold">{{ t('Barcode') }}:</span>
              <span class="ml-2">{{ data.barcode }}</span>
            </div>
            <div>
              <span class="font-weight-bold">{{ t('Reference No') }}:</span>
              <span class="ml-2">{{ data.reference_no }}</span>
            </div>
            <div class="d-flex justify-content-center align-items-center">
              <span class="text-center font-weight-bold">{{ t('Provider') }}:</span>
              <div class="provider-info">
                <img
                  :src="getProviderLogo(data.provider)"
                  :alt="data.provider.name + ' Logo'"
                  class="provider-logo ml-2"
                />
              </div>
            </div>
            <div>
              <b-button
                variant="primary"
                class="mb-1"
                type="submit"
                :disabled="submitting"
                @click="startProcessManually()"
              >
                {{ t('Save and Create Label') }}
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
      <b-row>
        <consignor-information-form
          :consignorInformations="consignorInformations"
          :editable="false"
        />
        <consignee-information-form
          :consigneeInformations="consigneeInformations"
          :editable="false"
        />
      </b-row>
      <b-row>
        <b-col>
          <b-card no-body>
            <h4 class="mb-1 font-weight-bold">
              {{ t('Package Informations') }}
            </h4>
            <consignment-package
              v-for="(item, index) in data.packages"
              :key="index"
              :title="`${t('Package')} ${index + 1}`"
              :data="item"
              :consignmentId="consignmentId"
              :class="[index !== 0 && 'mt-1']"
            />
          </b-card>
        </b-col>
        <b-col>
          <ConsignmentDocuments
            :documents="data.documents"
            :consignment-id="data.id"
            :permissions="data.permissions"
          />
        </b-col>
      </b-row>
    </div>
    <b-modal
      id="find-shipment-label-modal"
      ref="find-shipment-label-modal"
      :hide-footer="true"
      centered
      :title="t('Save and Create Label')"
    >
      <div class="mb-2">{{ t('Barcode') }}: {{ labelBarcode }}</div>

      <div class="create-consignment-file">
        <img v-if="labelImageFile" :src="labelImageFile" />
        <pdf v-else-if="labelPdfFile" :src="labelPdfFile" />
        <img v-else-if="labelUrl && !labelUrlIsPdf" :src="labelUrl" />
        <pdf v-else-if="labelUrl && labelUrlIsPdf" :src="labelUrl" />
      </div>

      <b-button
        variant="primary"
        type="button"
        size="md"
        class="p-5-5px mt-1"
        @click="downloadLabel"
      >
        {{ t('Download Document') }}
      </b-button>
    </b-modal>
  </div>
</template>
<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { BRow, BCol, BCard, BCardBody, BButton, BModal, BSpinner } from 'bootstrap-vue';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import ConsignorInformationForm from '@/components/ConsignorInformationForm.vue';
import ConsigneeInformationForm from '@/components/ConsigneeInformationForm.vue';
import ConsignmentPackage from '@/components/ConsignmentPackage.vue';
import ConsignmentDocuments from '@/views/pages/consignments/ConsignmentDocuments.vue';
import pdf from 'vue-pdf';
import { getProviderLogo } from '@/libs/providerHelper';

export default {
  name: 'BorderShipments',

  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BModal,
    pdf,
    BSpinner,

    ConsignmentPackage,
    ConsignmentDocuments,
    ConsignorInformationForm,
    ConsigneeInformationForm
  },

  data() {
    return {
      queryType: 'tracking_code',
      barcode: '',
      data: {},
      contentType: '',
      labelImageFile: null,
      labelPdfFile: null,
      labelBarcode: '',
      labelCache: null,
      labelUrl: '',
      labelUrlIsPdf: false,
      consignorInformations: {
        country: '',
        countryIso3: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: ''
      },
      consigneeInformations: {
        country: '',
        countryIso3: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: ''
      },
      disabled: true,
      queryContainerStatus: true,
      detailStatus: false,
      consignmentId: 0,
      submitting: false,
      pageLoading: false
    };
  },
  mixins: [helper],
  created() {
    if (this.$route.query.code || this.$route.query.barcode) {
      this.barcode = this.$route.query.code || this.$route.query.barcode;
      if (this.$route.query.type) {
        this.queryType = this.$route.query.type;
      }
      this.pageLoading = true;
      this.getConsignmentFindShipments();
    } else {
      this.$router.push('/find-shipment');
    }
  },
  methods: {
    setQueryType(type) {
      this.queryType = type;
      this.barcode = '';
      this.disabled = true;
    },
    goBack() {
      this.$router.push('/find-shipment');
    },
    openLabelModalFromBase64(base64, contentType, barcode) {
      this.contentType = contentType || '';
      if (this.contentType && this.contentType.startsWith('image/')) {
        this.labelImageFile = this.getBase64ImageFile(base64, this.contentType);
        this.labelPdfFile = null;
      } else {
        this.labelPdfFile = this.getBase64PdfFile(base64);
        this.labelImageFile = null;
      }
      this.labelUrl = '';
      this.labelUrlIsPdf = false;
      this.labelBarcode = barcode || this.data.barcode || '';
      this.$refs['find-shipment-label-modal'].show();
    },
    openLabelModalFromUrl(label) {
      if (!label) return;
      const fileName = (label.file_name || label.file_url || '').toLowerCase();
      this.labelUrlIsPdf = fileName.endsWith('.pdf');
      this.labelUrl = label.file_url;
      this.labelImageFile = null;
      this.labelPdfFile = null;
      this.labelBarcode = label.tracking_code || this.data.barcode || '';
      this.$refs['find-shipment-label-modal'].show();
    },
    getConsignmentFindShipments() {
      const request =
        this.queryType === 'tracking_code'
          ? useService.getConsigmentsFindShipments(this.barcode)
          : useService.getConsignmentByReferenceNo(this.barcode);

      request
        .then(response => {
          if (response.status) {
            this.consignmentId = response.data.id;
            this.detailStatus = true;
            this.data = response.data;
            this.consignorInformations = response.data.consignor;
            this.consigneeInformations = response.data.consignee;

            if (
              this.$route.query.code !== this.barcode ||
              this.$route.query.type !== this.queryType
            ) {
              this.$router
                .replace({
                  query: { ...this.$route.query, code: this.barcode, type: this.queryType }
                })
                .catch(() => {});
            }
          } else {
            this.showErrorMessage(this.$t(response.message || 'Consignment not found'));
            this.goBack();
          }
        })
        .catch(error => {
          this.showErrorMessage(this.$t(error.message || 'Consignment not found'));
          this.goBack();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    checkBarcodeValue() {
      if (this.barcode.length > 0) {
        this.disabled = false;
      }
    },
    startProcessManually() {
      // 1) Aynı oturumda tekrar tıklanırsa cache'ten göster
      if (this.labelCache && this.labelCache.base64label) {
        this.openLabelModalFromBase64(
          this.labelCache.base64label,
          this.labelCache.contentType,
          this.labelCache.barcode
        );
        return;
      }

      // 2) Sayfa yenilendiyse ve find endpoint'ten label url geldiyse, istek atmadan göster
      if (this.data && this.data.labels && this.data.labels.length > 0) {
        // Sadece label_type == 0 olan etiket gösterilsin
        const preferredLabel = this.data.labels.find(
          l => l && (l.label_type === 0 || l.label_type === '0')
        );
        if (preferredLabel) {
          this.openLabelModalFromUrl(preferredLabel);
          return;
        }
      }

      this.submitting = true;
      useService
        .startProcessManually(this.data.id)
        .then(payload => {
          const payloadData = payload.data || {};
          const base64 = payloadData.base64label || payload.base64label;
          const contentType =
            payloadData.contentType || payloadData.content_type || payload.contentType || '';

          if (base64) {
            // cache'le ve modalı aç
            this.labelCache = {
              base64label: base64,
              contentType,
              barcode: payloadData.barcode || payload.barcode || this.data.barcode || ''
            };
            this.openLabelModalFromBase64(
              this.labelCache.base64label,
              this.labelCache.contentType,
              this.labelCache.barcode
            );
            return;
          }

          if (payload.status) {
            this.showSuccessMessage(
              `${payload.message || this.$t('Sending to provider successful')}`
            );
            return;
          }

          this.showErrorMessage(this.$t('An error occurred'));
        })
        .catch(errors => {
          if (Array.isArray(errors)) {
            errors.forEach(item => this.showErrorMessage(item.message));
            return;
          }
          if (errors && errors.message) {
            this.showErrorMessage(errors.message);
            return;
          }
          this.showErrorMessage(this.$t('An error occurred'));
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    downloadLabel() {
      let fileContent;
      let fileFormat;

      // URL'den görüntülüyorsak servis ile indir
      if (this.labelUrl) {
        useService
          .downloadLabelDocument(this.labelUrl)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'labelFile');
            document.body.appendChild(link);
            link.click();
          })
          .finally(() => {});
        return;
      }

      if (this.contentType && this.contentType.startsWith('image/')) {
        fileContent = this.labelImageFile;
        fileFormat = this.contentType.split('/')[1];
      } else {
        fileContent = this.labelPdfFile;
        fileFormat = 'pdf';
      }

      const fileName = `labelFile.${fileFormat}`;

      const downloadLink = document.createElement('a');
      downloadLink.href = fileContent;
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    },
    getProviderLogo
  },
  mounted() {},
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
